<template>
  <div ref="root" class="chart-root" style="width: 100%"></div>
</template>

<script>

import { debounce, cloneDeep, defaultsDeep, get } from 'lodash'
import $ from 'jquery'

require('c3/c3.css')

export default {
  name: 'C3Chart',
  props: {
    height: {
      type: Number,
      default: 300
    },
    config: {
      type: Object,
      default: () => ({})
    },
    data: {
      type: Object,
      default: () => ({})
    },
    type: {
      type: String,
      default: 'line'
    }
  },
  watch: {
    data: {
      handler: 'reload',
      deep: true
    },
    type: 'transform'
  },
  methods: {
    /**
     * Forces the chart width to 1px which will force a re-draw to the width of
     * the parent container (see the onrendered handler below)
     *
     * @return undefined
     */
    fit () {
      if (this.$chart) {
        this.handleResize()
      }
    },

    getArgs () {
      const data = this.getData()
      const config = this.getConfig()
      return defaultsDeep({ data }, config)
    },
    getData () {
      const { type } = this
      const data = cloneDeep(this.data)
      return defaultsDeep({ type }, data)
    },
    handleResize () {
      this.$chart.resize({
        height: this.height,
        width: 1
      })
    },
    /**
     * I found that the chart would expand to fit the width of the parent
     * element but never resize back down when the window was narrowed. The
     * onresized and onrendered handlers below work together to resize the
     * chart into the correct width by setting the chart's width to 1px when
     * the window is resized (allowing the parent div to resize to it's
     * natural width) and then once rendered if the chart is 1px wide
     * re-size to the parent div's width
     *
     * =====================================================================
     *
     * dlundy's notes: this is effectively a workaround for the fact that if we resize the chart to be bigger, the
     * container and parent elements will grow with it, and then we have no easy way of determining when we should
     * shrink it (short of trying to calculate it from the browser window size, or something.)
     *
     * By resizing the chart back down to 1px in handleResize() we can then make another pass in handleRender() below
     * and fit to the correct current size of the 'root' container  element. The container element is set to 100% width
     * so it fills the parent of this component.
     */
    handleRender () {
      const rootWidth = $(this.$refs.root).innerWidth()

      if (get(this, '$chart.internal.currentWidth') === 1) {
        // Prevent an infinate loop if the parent element is also 1 px wide.
        // If the root element is actually 1px then we're already sized
        // correctly by the onresized function above
        if (rootWidth > 1) {
          this.$chart.resize({
            height: this.height,
            width: rootWidth
          })
        }
      }
    },

    getConfig () {
      const config = cloneDeep(this.config)

      const defaults = {
        color: {
          pattern: ['#195152', '#7C931F', '#0A88C2', '#D97706', '#DB2828', '#709D91', '#8C564B', '#A9B4BA']
        },
        axis: {
          x: {
            type: 'category',
            padding: {
              left: 0,
              right: 0
            },
            tick: {
              multiline: true,
              centered: true
            }
          }
        },
        size: {
          height: this.height
        },
        onresized: this.handleResize,
        onrendered: this.handleRender
      }

      return defaultsDeep(config, defaults)
    },
    update: debounce(function update () {
      const data = this.getData()
      this.$chart.load(data)
      this.$emit('update', data)
    }, 500),
    transform: debounce(function transform (...args) {
      this.$chart.transform(...args)
    }, 100),
    reload: debounce(function reload () {
      this.$emit('reloading')
      this.$chart.unload()
      this.$nextTick(() => {
        this.update()
      })
    }, 700),
    regenerate: debounce(function regenerate () {
      this.$chart.destroy()
      this.$chart = this.$c3.generate({
        bindto: this.$refs.root,
        ...this.getArgs()
      })
    }, 700),

    /**
     * Stub function to be overridden by extending vue component if any
     * additional tasks are to be executed after the chart is initialized.
     */
    afterInitHook: () => {
      return undefined
    }
  },
  mounted () {
    const args = this.getArgs()
    this.$chart = this.$c3.generate({
      bindto: this.$refs.root,
      ...args
    })
    this.afterInitHook()
    this.$emit('init', args)
  },
  beforeDestroy () {
    this.$chart = this.$chart.destroy()
  }
}
</script>
